/**
 * Centralized API Client
 *
 * Provides fetch wrapper that includes X-API-Key header for all requests.
 * Use this instead of raw fetch() for all backend API calls.
 *
 * Handles 401 Unauthorized by triggering session expiration flow.
 */

import { handleUnauthorized } from '../store/sessionStore';

const API_KEY = import.meta.env.VITE_API_KEY || '';

// URLs that should NOT trigger session expiration on 401
// (auth endpoints handle their own 401s)
const AUTH_ENDPOINTS = ['/auth/pin/status', '/auth/pin/verify', '/auth/pin/logout'];

/**
 * Check if URL is an auth endpoint (shouldn't trigger session expiration)
 */
function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

/**
 * Default headers for all API requests
 */
function getDefaultHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }

  return headers;
}

/**
 * Fetch wrapper that automatically includes credentials (cookies)
 * and handles 401 Unauthorized by triggering session expiration.
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = {
    ...getDefaultHeaders(),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Send cookies for session auth
  });

  // Handle 401 Unauthorized - session expired
  if (response.status === 401 && !isAuthEndpoint(url)) {
    handleUnauthorized();
  }

  return response;
}

/**
 * GET request helper
 */
export async function apiGet<T>(url: string): Promise<T> {
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * POST request helper
 */
export async function apiPost<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiFetch(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * PUT request helper
 */
export async function apiPut<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiFetch(url, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * PATCH request helper
 */
export async function apiPatch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiFetch(url, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * DELETE request helper
 */
export async function apiDelete<T>(url: string): Promise<T> {
  const response = await apiFetch(url, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get API key for Socket.IO auth
 */
export function getApiKey(): string {
  return API_KEY;
}
